/**
 * Session Service - Ersetzt Token-basierte Authentifizierung
 * 
 * Features:
 * - Session-ID im sessionStorage (wird bei Browser-Close gelöscht)
 * - Server-basierte Session-Verwaltung
 * - Browser-Close Detection
 * - Auto-Logout Integration
 * - Heartbeat für Session-Alive
 */

import axios from 'axios';
// createApiUrl wird dynamisch importiert um Initialisierungsprobleme zu vermeiden

// Lokale URL-Erstellung um Initialisierungsprobleme zu vermeiden
const buildApiUrl = (endpoint) => {
  // Prüfe zuerst auf Umgebungsvariable
  if (process.env.REACT_APP_API_URL) {
    return `${process.env.REACT_APP_API_URL}${endpoint.startsWith('/') ? endpoint : '/' + endpoint}`;
  }
  
  const protocol = window.location.protocol;
  const hostname = window.location.hostname;
  
  // In Entwicklungsumgebung: localhost mit Backend-Port
  const baseUrl = (hostname === 'localhost' || hostname === '127.0.0.1') 
    ? `${protocol}//${hostname}:25000`
    : `${protocol}//${hostname}`;  // In Produktion: kein Port (Nginx proxy)
  
  return `${baseUrl}${endpoint.startsWith('/') ? endpoint : '/' + endpoint}`;
};

class SessionService {
  constructor() {
    this.sessionId = null;
    this.currentUser = null;
    this.heartbeatInterval = null;
    this.activityCheckInterval = null;
    this.autoLogoutTimeout = 10; // Default 10 minutes
    this.warningCallback = null;
    this.warningShown = false;
    this.configLoaded = false; // Flag um zu verfolgen ob Config geladen wurde
    
    // console.log('🚀 SESSION: SessionService constructor starting');
    
    this.setupAxiosInterceptors();
    this.setupBrowserCloseDetection();
    
    // Session-Initialisierung wird asynchron gestartet
    // aber nicht im Konstruktor gewartet (würde blockieren)
    this.sessionInitialized = false;
    this.initializationPromise = this.initializeSession().then((success) => {
      this.sessionInitialized = true;
      return success;
    });
    
    // Config NICHT im Konstruktor laden - Module sind noch nicht vollständig initialisiert
    // Wird beim Login oder bei Session-Wiederherstellung geladen
    
    // Activity Tracking IMMER starten, auch ohne Session (für spätere Anmeldung)
    this.startActivityTracking();
    
    // console.log('✅ SESSION: SessionService constructor completed');
  }

  // =====================================================================
  // SESSION MANAGEMENT
  // =====================================================================

  async login(username, password) {
    try {
      // Login-Versuch
      
      const formData = new URLSearchParams();
      formData.append('username', username);
      formData.append('password', password);

      const response = await axios.post(
        buildApiUrl('/auth/session/login'),
        formData,
        {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
        }
      );

      if (response.data && response.data.session_id) {
        this.sessionId = response.data.session_id;
        this.currentUser = response.data.user;
        
        // Session-ID in sessionStorage speichern (wird bei Browser-Close gelöscht)
        sessionStorage.setItem('session_id', this.sessionId);
        sessionStorage.setItem('user_data', JSON.stringify(this.currentUser));
        
        // Config speichern falls vorhanden
        if (response.data.config) {
          console.log('🔐 SESSION SERVICE LOGIN: Config empfangen:', response.data.config);
          sessionStorage.setItem('app_config', JSON.stringify(response.data.config));
          console.log('🔐 SESSION SERVICE LOGIN: Config in sessionStorage gespeichert');
        } else {
          console.warn('⚠️ SESSION SERVICE LOGIN: Keine Config in Response erhalten!');
        }
        
        // Activity tracking starten
        this.startActivityTracking();
        
        // Auto-Logout Konfiguration laden
        await this.loadAutoLogoutConfig();
        
        this.startHeartbeat();
        
        // Login erfolgreich, Session erstellt
        return { success: true, user: this.currentUser };
      }
      
      return { success: false, error: 'No session data received' };
      
    } catch (error) {

      
      // Prüfe ob es ein Netzwerk-/Server-Erreichbarkeitsfehler ist
      if (!error.response) {
        // Kein Response = Server nicht erreichbar (Network Error, Timeout, etc.)
        return {
          success: false,
          error: 'Server not reachable',
          errorCode: 'SERVER_NOT_REACHABLE'
        };
      }
      
      // Spezifische Fehlercodes vom Backend verarbeiten
      const errorDetail = error.response?.data?.detail;
      let errorCode = null;
      
      if (errorDetail && typeof errorDetail === 'object' && errorDetail.error_code) {
        errorCode = errorDetail.error_code;
      }
      
      return { 
        success: false, 
        error: errorDetail?.message || errorDetail || 'Login failed',
        errorCode: errorCode
      };
    }
  }

  async logout() {
    try {
      // Logout wird durchgeführt
      
      if (this.sessionId) {
        await axios.post(buildApiUrl('/auth/session/logout'), {
          session_id: this.sessionId
        });
      }
      
      this.clearSession();
      // Logout erfolgreich
      
    } catch (error) {
      // Logout-Fehler (Session wird trotzdem gelöscht)
      this.clearSession();
    }
  }

  async validateSession() {
    try {
      if (!this.sessionId) {
        return { valid: false, error: 'No session ID' };
      }

      // Session-ID wird als Header gesendet (konsistent mit anderen Requests)
      const response = await axios.get(buildApiUrl('/auth/session/validate'), {
        headers: {
          'X-Session-ID': this.sessionId
        }
      });

      if (response.data && response.data.valid) {
        this.currentUser = response.data.user;
        sessionStorage.setItem('user_data', JSON.stringify(this.currentUser));
        
        // Config speichern falls vorhanden
        if (response.data.config) {
          console.log('🔐 SESSION SERVICE VALIDATE: Config empfangen:', response.data.config);
          sessionStorage.setItem('app_config', JSON.stringify(response.data.config));
          console.log('🔐 SESSION SERVICE VALIDATE: Config in sessionStorage gespeichert');
        } else {
          console.warn('⚠️ SESSION SERVICE VALIDATE: Keine Config in Response erhalten!');
        }
        
        return { valid: true, user: this.currentUser };
      }
      
      return { valid: false, error: 'Session validation failed' };
      
    } catch (error) {
      // Session-Validierung fehlgeschlagen
      return { 
        valid: false, 
        error: error.response?.data?.detail || 'Session invalid' 
      };
    }
  }

  clearSession() {
    // Session-Daten werden gelöscht
    
    this.sessionId = null;
    this.currentUser = null;
    this.warningShown = false;
    
    sessionStorage.removeItem('session_id');
    sessionStorage.removeItem('user_data');
    sessionStorage.removeItem('app_config');
    sessionStorage.removeItem('lastActivity');
    
    this.stopHeartbeat();
    this.stopActivityTracking();
  }

  async initializeSession() {
    // Session aus sessionStorage wiederherstellen
    const storedSessionId = sessionStorage.getItem('session_id');
    const storedUserData = sessionStorage.getItem('user_data');
    
    if (storedSessionId && storedUserData) {
      this.sessionId = storedSessionId;
      try {
        this.currentUser = JSON.parse(storedUserData);
        
        // Activity Tracking SOFORT starten, bevor Session validiert wird
        this.startActivityTracking();
        
        // Session validieren - WARTEN auf Ergebnis
        const result = await this.validateSession();
        if (!result.valid) {
          // Session-Validierung fehlgeschlagen
          this.clearSession();
          return false;
        } else {
          // Session-Validierung erfolgreich
          // Auto-Logout Konfiguration laden
          await this.loadAutoLogoutConfig();
          this.startHeartbeat();
          return true;
        }
      } catch (error) {
        // Fehler beim Parsen der gespeicherten Benutzerdaten
        this.clearSession();
        return false;
      }
    } else {
      // Keine gespeicherte Session gefunden
      return false;
    }
  }

  getSessionId() {
    return this.sessionId;
  }

  getCurrentUser() {
    return this.currentUser;
  }

  setCurrentUser(user) {
    this.currentUser = user;
  }

  isAuthenticated() {
    return !!this.sessionId && !!this.currentUser;
  }

  getConfig() {
    const configString = sessionStorage.getItem('app_config');
    console.log('🔐 SESSION SERVICE getConfig: Raw config string:', configString);
    if (configString) {
      try {
        const parsed = JSON.parse(configString);
        console.log('🔐 SESSION SERVICE getConfig: Parsed config:', parsed);
        return parsed;
      } catch (error) {
        console.error('❌ SESSION SERVICE getConfig: Error parsing app config:', error);
        return {};
      }
    }
    console.warn('⚠️ SESSION SERVICE getConfig: Kein app_config in sessionStorage gefunden!');
    return {};
  }

  // Warte auf Session-Initialisierung
  async waitForInitialization() {
    if (this.sessionInitialized) {
      return this.isAuthenticated();
    }
    return await this.initializationPromise;
  }

  // =====================================================================
  // BROWSER CLOSE DETECTION
  // =====================================================================

  setupBrowserCloseDetection() {
    // Browser Close Detection - sessions werden automatisch durch sessionStorage gelöscht
    window.addEventListener('beforeunload', (event) => {
      // Session wird automatisch ungültig wenn sessionStorage gelöscht wird

    });

    // Backup: Bei Visibility Change prüfen
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {

      } else {

        // Session re-validieren nach Page-Return
        if (this.sessionId) {
          this.validateSession();
        }
      }
    });
  }

  // =====================================================================
  // RELOAD AUTO-LOGOUT CONFIG (für Admin-Änderungen)
  // =====================================================================
  
  async reloadAutoLogoutConfig() {

    await this.loadAutoLogoutConfig();
  }

  // =====================================================================
  // HEARTBEAT & ACTIVITY TRACKING
  // =====================================================================

  startHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
    }
    
    // Heartbeat nur bei längeren Auto-Logout-Zeiten
    if (this.autoLogoutTimeout >= 5) {
      // Mindestens 5 Minuten für Heartbeat
      const heartbeatInterval = Math.min(60000, this.autoLogoutTimeout * 60 * 1000 * 0.5); // Max 60s oder 50% der Auto-Logout Zeit
      
              // Heartbeat aktiviert
      
      this.heartbeatInterval = setInterval(() => {
        this.sendHeartbeat();
      }, heartbeatInterval);
    } else {
      // Heartbeat deaktiviert (Auto-Logout-Zeit zu kurz)
      this.heartbeatInterval = null;
    }
  }

  stopHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  async sendHeartbeat() {
    try {
      if (!this.sessionId) return;
      
      // HEARTBEAT OHNE Activity-Tracking - soll NICHT als Benutzeraktivität gelten
      await axios.post(buildApiUrl('/auth/session/heartbeat'), {
        session_id: this.sessionId
      }, {
        headers: {
          'X-Skip-Activity-Tracking': 'true' // Flag um Activity-Update zu verhindern
        }
      });
      
      // Heartbeat gesendet
      
    } catch (error) {
      // Heartbeat fehlgeschlagen, Session möglicherweise abgelaufen
      this.clearSession();
      // Redirect to login
      window.location.href = '/login';
    }
  }

  startActivityTracking() {
    // Activity-Tracking wird gestartet
    
    // Stoppe existierende Timer falls vorhanden
    this.stopActivityTracking();
    
    // Track User-Aktivität
    const trackActivity = (eventType = 'unknown') => {
      const now = Date.now();
      const wasWarningShown = this.warningShown;
      sessionStorage.setItem('lastActivity', now.toString());
      this.warningShown = false; // Reset warning bei Aktivität
      
      // Nur bei erstem Aufruf oder Warning-Reset loggen
      if (eventType === 'initial' || wasWarningShown) {
        // Aktivität getrackt
      }
    };

    // Initial activity
    trackActivity('initial');

    // Event Listener für User-Aktivität
    const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    // Activity-Listener werden hinzugefügt
    
    activityEvents.forEach(event => {
      const listener = () => trackActivity(event);
      document.addEventListener(event, listener, true);
      // Store listeners for cleanup
      if (!this.activityListeners) {
        this.activityListeners = [];
      }
      this.activityListeners.push({ event, listener });
    });

    // Regelmäßige Prüfung auf Inaktivität (alle 30 Sekunden)
    // Inaktivitäts-Timer wird gestartet (30s Intervall)
    
    this.activityCheckInterval = setInterval(() => {
      this.checkInactivity();
    }, 30000);
    
    // Activity-Tracking erfolgreich gestartet
  }

  stopActivityTracking() {
    // Activity-Tracking wird gestoppt
    
    if (this.activityCheckInterval) {
      clearInterval(this.activityCheckInterval);
      this.activityCheckInterval = null;
      // Inaktivitäts-Timer gestoppt
    }
    
    // Remove all activity event listeners
    if (this.activityListeners) {
      this.activityListeners.forEach(({ event, listener }) => {
        document.removeEventListener(event, listener, true);
      });
      this.activityListeners = [];
      // Activity-Listener entfernt
    }
    
    // Activity-Tracking gestoppt
  }

  checkInactivity() {
    if (!this.sessionId) {
      return;
    }

    const lastActivity = sessionStorage.getItem('lastActivity');
    if (!lastActivity) {
      return;
    }

    const now = Date.now();
    const lastActivityTime = parseInt(lastActivity);
    const inactiveMinutes = (now - lastActivityTime) / (1000 * 60);

    // Auto-Logout Warnung bei konfiguriertem Wert
    if (inactiveMinutes >= this.autoLogoutTimeout && !this.warningShown) {

      this.warningShown = true;
      this.showAutoLogoutWarning();
    }
  }

  showAutoLogoutWarning() {
    if (this.warningCallback && typeof this.warningCallback === 'function') {

      try {
        this.warningCallback();
      } catch (error) {
        console.error('❌ SESSION: Error calling warningCallback:', error);
      }
    } else {
      console.error('❌ SESSION: No valid warningCallback available!');
    }
  }

  setWarningCallback(callback) {
    this.warningCallback = callback;
    // console.log(`🔗 SESSION: Auto-logout warning callback ${callback ? 'registered' : 'removed'}`);
  }

  resetActivity() {
    sessionStorage.setItem('lastActivity', Date.now().toString());
    this.warningShown = false;
  }



  // =====================================================================
  // AUTO-LOGOUT CONFIG
  // =====================================================================

  async loadAutoLogoutConfig() {
    try {
      // Auto-Logout-Konfiguration wird vom Server geladen
      
      const apiUrl = buildApiUrl('/config/autologout');
      
      const response = await axios.get(apiUrl);
      
      if (response.data.success) {
        this.autoLogoutTimeout = response.data.autologout_minutes;
        this.configLoaded = true;
        
        // Activity Tracking mit neuer Konfiguration neu starten
        this.stopActivityTracking();
        this.startActivityTracking();
        
        // Heartbeat neu konfigurieren bei geänderter Auto-Logout-Zeit
        if (this.sessionId) {
          this.startHeartbeat();
        }
      } else {
        // Auto-Logout-Konfiguration nicht erfolgreich
      }
    } catch (error) {
      // Auto-Logout-Konfiguration konnte nicht geladen werden, verwende Standard
    }
  }

  // =====================================================================
  // AXIOS INTERCEPTORS
  // =====================================================================

  setupAxiosInterceptors() {
    // Request interceptor - Session-ID zu allen Requests hinzufügen
    axios.interceptors.request.use(
      (config) => {
        if (this.sessionId) {
          config.headers['X-Session-ID'] = this.sessionId;
        }
        
        // Activity tracking NUR bei echten User-Aktionen, NICHT bei Heartbeat oder automatischen Requests
        const isHeartbeat = config.url?.includes('/heartbeat') || 
                           config.headers['X-Skip-Activity-Tracking'] === 'true';
        const isAutomaticRequest = config.url?.includes('/system-status') ||
                                  config.url?.includes('/config/autologout') ||
                                  config.url?.includes('/session/validate');
        
        if (this.sessionId && !isHeartbeat && !isAutomaticRequest) {
          const wasWarningShown = this.warningShown;
          sessionStorage.setItem('lastActivity', Date.now().toString());
          this.warningShown = false; // Reset warning bei echter Aktivität
          
          if (wasWarningShown) {
            // Aktivität durch API-Aufruf getrackt - Warnung zurückgesetzt
          }
        }
        
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor - Session-Fehler abfangen
    axios.interceptors.response.use(
      (response) => {
        return response;
      },
      (error) => {
        // 401 bei Login-Endpunkten NICHT abfangen - das sind normale Authentifizierungsfehler
        const isLoginRequest = error.config?.url?.includes('/auth/session/login') || 
                              error.config?.url?.includes('/auth/login');
        
        if (error.response?.status === 401 && !isLoginRequest) {

          this.clearSession();
          
          // Redirect zu Login falls nicht bereits dort
          if (!window.location.pathname.includes('/login')) {
            window.location.href = '/login';
          }
        }
        return Promise.reject(error);
      }
    );
  }
}

// Singleton instance
const sessionService = new SessionService();

export default sessionService;

// Export auch einzelne Funktionen für Backward-Compatibility
export const getSessionId = () => sessionService.getSessionId();
export const getCurrentUser = () => sessionService.getCurrentUser();
export const isAuthenticated = () => sessionService.isAuthenticated();
export const getConfig = () => sessionService.getConfig();
export const waitForInitialization = () => sessionService.waitForInitialization();
export const login = (username, password) => sessionService.login(username, password);
export const logout = () => sessionService.logout();
export const validateSession = () => sessionService.validateSession();




